import moment from 'moment'
import _ from 'lodash'
import {getHabilitations} from '../utils/habilitations'
import {basicContext} from "../../../utils/contextUtils"
import {groupIdForMongoQuery, sortObjectForMongoQuery} from "../engines/alert/mongoPipeline"

// Fiscal year start to date / Calendar year start to date / Rolling year → monthly buckets
const MONTHLY_WINDOW_IDS = new Set(['00000000000000000000000d', '000000000000000000000002', '000000000000000000000001'])
// Monthly / Rolling month → weekly buckets (weeks start on the 1st of the month)
const WEEKLY_WINDOW_IDS = new Set(['00000000000000000000000c', '00000000000000000000000f'])
// Weekly (Mon→Sun) / Rolling week → daily buckets (default)

const getGroupStage = (timeWindowId, sumObject) => {
    if (MONTHLY_WINDOW_IDS.has(timeWindowId)) {
        return {
            $group: {
                _id: { $dateToString: { format: '%Y-%m', date: '$date.lt' } },
                number: { $sum: 1 },
                ...sumObject
            }
        }
    }
    if (WEEKLY_WINDOW_IDS.has(timeWindowId)) {
        return {
            $group: {
                _id: {
                    year: { $year: '$date.lt' },
                    month: { $month: '$date.lt' },
                    week: { $ceil: { $divide: [{ $dayOfMonth: '$date.lt' }, 7] } }
                },
                number: { $sum: 1 },
                ...sumObject
            }
        }
    }
    // Daily (DAILY_WINDOW_IDS)
    return {
        $group: {
            _id: { $dateToString: { format: '%Y-%m-%d', date: '$date.lt' } },
            number: { $sum: 1 },
            ...sumObject
        }
    }
}

const getSortStage = timeWindowId => {
    if (WEEKLY_WINDOW_IDS.has(timeWindowId)) {
        return { $sort: { '_id.year': 1, '_id.month': 1, '_id.week': 1 } }
    }
    return { $sort: { _id: 1 } }
}

const getWeekPrefix = languageId => {
    if (languageId === 'fr') return 'Sem'
    if (languageId === 'it') return 'Sett'
    return 'W'
}

const formatDateLabel = (id, timeWindowId, weekPrefix) => {
    if (WEEKLY_WINDOW_IDS.has(timeWindowId)) {
        return `${id.year}-${String(id.month).padStart(2, '0')}-${weekPrefix}${id.week}`
    }
    return id
}

const generateAllBuckets = (timeWindowId, startDate, endDate, themeKeyObjects, weekPrefix) => {
    const zeroThemeKeys = themeKeyObjects.reduce((acc, tk) => ({ ...acc, [tk.id]: 0 }), {})
    const emptyBucket = date => ({ date, number: 0, ...zeroThemeKeys })
    const buckets = []

    if (MONTHLY_WINDOW_IDS.has(timeWindowId)) {
        const current = startDate.clone().startOf('month')
        while (current.isSameOrBefore(endDate, 'month')) {
            buckets.push(emptyBucket(current.format('YYYY-MM')))
            current.add(1, 'month')
        }
    } else if (WEEKLY_WINDOW_IDS.has(timeWindowId)) {
        const current = startDate.clone().startOf('month')
        while (current.isSameOrBefore(endDate, 'month')) {
            const year = current.year()
            const month = current.month() + 1
            const weeksInMonth = Math.ceil(current.daysInMonth() / 7)
            const firstWeek = current.isSame(startDate, 'month')
                ? Math.ceil(startDate.date() / 7)
                : 1
            for (let w = firstWeek; w <= weeksInMonth; w++) {
                buckets.push(emptyBucket(`${year}-${String(month).padStart(2, '0')}-${weekPrefix}${w}`))
            }
            current.add(1, 'month')
        }
    } else {
        const current = startDate.clone().startOf('day')
        while (current.isSameOrBefore(endDate, 'day')) {
            buckets.push(emptyBucket(current.format('YYYY-MM-DD')))
            current.add(1, 'day')
        }
    }

    return buckets
}

async function findData(context) {
    const [storeHabilitations, orgHabilitations] = await getHabilitations(context)
    const stores = storeHabilitations.map(hab => _.get(hab, 'store._id'))
    const organizations = orgHabilitations.map(hab => _.get(hab, 'organization._id'))

    const habQuery = stores.length || organizations.length
        ? {
            $or: [
                {store: {$in: stores}},
                {organizations: {$in: organizations}}
            ]
        }
        : {}
    const groupQuery = { group: new global.ObjectID(context.group.id) }

    const alertConfigurationId = _.get(context, 'clientContext.alertConfiguration')
    const timeWindowId = _.get(context, 'clientContext.timeWindow')
    const weekPrefix = getWeekPrefix(_.get(context, 'language.id'))

    const generalSettings = await global.app.S.GeneralSettings.collection.findOne({group: new global.ObjectID(context.group.id)})
    const alertConfiguration = await global.app.S.AlertConfiguration.get(
        alertConfigurationId,
        {
            ...basicContext(context),
            fieldPath: ['id', 'timeWindow.id', 'dashboardThemeKeysObjects', 'groupAxes.sortObjectForMongo', 'groupAxes.groupIdObjectForMongo']
        }
    )
    const timeWindow = global.app.S.TimeWindow.objects.find(object => object.id === timeWindowId)

    const fiscalYearStartDate = `${generalSettings.monthNumber}-${generalSettings.month}`
    const fiscalYearStart = moment.utc(fiscalYearStartDate, 'DD-MM')

    const endDate = moment.utc().subtract(generalSettings.delay, 'days')
    const endDatePlusOne = moment.utc().subtract(generalSettings.delay, 'days').add(1, 'days')
    let startDate
    if (timeWindow.id === '00000000000000000000000d') {
        // Fiscal year start to date
        startDate = endDatePlusOne.isSameOrAfter(fiscalYearStart)
            ? fiscalYearStart
            : fiscalYearStart.subtract(1, 'years')
    } else if (timeWindow.id === '00000000000000000000000b') {
        // 2_weekly: start of current ISO week (Monday)
        startDate = endDate.clone().startOf('isoWeek')
    } else if (timeWindow.id === '00000000000000000000000c') {
        // 3_monthly: start of current month
        startDate = endDate.clone().startOf('month')
    } else if (timeWindow.id === '000000000000000000000002') {
        // 8_calendarYear: start of current year
        startDate = endDate.clone().startOf('year')
    } else {
        startDate = endDate.clone().subtract(timeWindow.quantity, timeWindow.period)
    }

    const dashboardDates = [startDate.format('DD-MM-YYYY'), endDate.format('DD-MM-YYYY')]

    const datesQuery = {
        'date.lt': { $gte: startDate.toDate(), $lte: endDatePlusOne.toDate() }
    }

    const sumObject = alertConfiguration.dashboardThemeKeysObjects.reduce((acc, themeKeyObject) => ({
        ...acc,
        [themeKeyObject.prefix]: { $sum: `$${themeKeyObject.path}` }
    }), {})

    const fiscalYearPipeline = ['00000000000000000000000d', '000000000000000000000002'].includes(alertConfiguration.timeWindow.id)
        ? [
            {
                $sort: {
                    ...sortObjectForMongoQuery(alertConfiguration.groupAxes),
                    alertCreationTime: -1
                }
            },
            {
                $group: {
                    _id: groupIdForMongoQuery(alertConfiguration.groupAxes),
                    firstElement: { $first: '$$ROOT' }
                }
            },
            {
                $replaceRoot: { newRoot: '$firstElement' }
            }
        ]
        : []

    const alerts = await global.app.S.Alert.collection
        .aggregate([
            {
                $match: {
                    ...datesQuery,
                    alertConfiguration: new global.ObjectID(alertConfiguration.id),
                    ...habQuery,
                    ...groupQuery
                }
            },
            ...fiscalYearPipeline,
            getGroupStage(timeWindowId, sumObject),
            getSortStage(timeWindowId)
        ])
        .toArray()

    const totalAlertsNumber = alerts.reduce((acc, group) => acc + group.number, 0)

    const alertData = alerts.map(o => ({
        date: formatDateLabel(o._id, timeWindowId, weekPrefix),
        number: o.number,
        ...alertConfiguration.dashboardThemeKeysObjects.reduce((acc, themeKeyObject) => ({
            ...acc,
            [themeKeyObject.id]: o[themeKeyObject.prefix]
        }), {})
    }))

    const alertDataMap = new Map(alertData.map(d => [d.date, d]))
    const allBuckets = generateAllBuckets(timeWindowId, startDate, endDate, alertConfiguration.dashboardThemeKeysObjects, weekPrefix)
    const data = allBuckets.map(bucket => alertDataMap.get(bucket.date) || bucket)

    return [
        {
            id: _.get(context, 'clientContext.elementId'),
            total: totalAlertsNumber,
            subTitle: {
                text: `${dashboardDates[0]} au ${dashboardDates[1]}`,
                font: '11px',
                color: '#888383'
            },
            data
        }
    ]
}

export const entity = {
    name: 'CustomDashboardTimeElement',
    dashboard: true,
    fields: ['id', 'total', 'subTitle', 'data'],
    find: function(context, callback) {
        this.prepareContext(context, 'L', (error, context) => {
            if (error) callback(error)
            else
                findData(context)
                    .then(objects => callback(null, objects))
                    .catch(error => callback(error))
        })
    }
}
